import { useState, useEffect, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import './ChartWidget.css';
import CandlestickTracker from '../../lib/CandlestickTracker';
import { createLogger } from '../../utils/logger';

const log = createLogger('ChartWidget');

type Candle = { x: number; y: [number, number, number, number] };

const ChartWidget = ({ selectedStock }: { selectedStock: string }) => {
    const [candlestickData, setCandlestickData] = useState<Candle[]>([]);

    useEffect(() => {
        const updateChartData = (allData: any) => {
            log.debug('Candlestick data update', {
                selectedStock,
                tickers: allData ? Object.keys(allData) : [],
            });
            if (allData[selectedStock] && allData[selectedStock].length > 0) {
                const formattedData: Candle[] = allData[selectedStock]
                    .filter((candle: any) => candle.x && candle.y?.length === 4)
                    .map((candle: any) => ({
                        x: new Date(candle.x).getTime(),
                        y: [...candle.y] as [number, number, number, number],
                    }));

                log.debug('Updating chart series', {
                    selectedStock,
                    points: formattedData.length,
                });
                setCandlestickData(formattedData);
            } else {
                log.warn('No valid candlestick data available', { selectedStock });
                setCandlestickData([]);
            }
        };

        CandlestickTracker.subscribe(updateChartData);
        updateChartData(CandlestickTracker.getAllCandlestickData());
        return () => CandlestickTracker.unsubscribe(updateChartData);
    }, [selectedStock]);

    const priceInfo = useMemo(() => {
        if (candlestickData.length === 0) return null;
        const first = candlestickData[0];
        const last = candlestickData[candlestickData.length - 1];
        const sessionOpen = first.y[0];
        const lastClose = last.y[3];
        const change = lastClose - sessionOpen;
        const pct = (change / sessionOpen) * 100;
        return { first, last, sessionOpen, lastClose, change, pct, isUp: change >= 0 };
    }, [candlestickData]);

    const minPrice =
        candlestickData.length > 0
            ? Math.min(...candlestickData.map((d) => d.y[2])) * 0.995
            : undefined;
    const maxPrice =
        candlestickData.length > 0
            ? Math.max(...candlestickData.map((d) => d.y[1])) * 1.005
            : undefined;

    const options: ApexOptions = {
        chart: {
            type: 'candlestick',
            background: 'transparent',
            animations: { enabled: false },
            toolbar: {
                show: true,
                tools: {
                    download: false,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                    reset: true,
                },
                autoSelected: 'zoom',
            },
            zoom: { enabled: true },
        },
        theme: { mode: 'dark' },
        plotOptions: {
            candlestick: {
                colors: {
                    upward: '#26a69a',
                    downward: '#ef5350',
                },
                wick: { useFillColor: true },
            },
        },
        grid: {
            borderColor: '#2a2a2a',
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
        },
        xaxis: {
            type: 'datetime',
            tooltip: { enabled: false },
            labels: {
                style: { colors: '#777777', fontSize: '11px' },
                formatter: (val: string, timestamp?: number) => {
                    const ts = timestamp ?? Number(val);
                    return new Intl.DateTimeFormat('en-US', {
                        timeZone: 'America/New_York',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                    }).format(new Date(ts));
                },
            },
            title: {
                text: 'ET',
                style: { color: '#555555', fontSize: '10px', fontWeight: 400 },
            },
            axisBorder: { color: '#333333' },
            axisTicks: { color: '#333333' },
        },
        yaxis: {
            min: minPrice,
            max: maxPrice,
            tooltip: { enabled: true },
            labels: {
                style: { colors: '#777777', fontSize: '11px' },
                formatter: (val: number) => val.toFixed(2),
            },
        },
        tooltip: {
            theme: 'dark',
            custom: ({ seriesIndex, dataPointIndex, w }: any) => {
                const o = w.globals.seriesCandleO[seriesIndex]?.[dataPointIndex];
                const h = w.globals.seriesCandleH[seriesIndex]?.[dataPointIndex];
                const l = w.globals.seriesCandleL[seriesIndex]?.[dataPointIndex];
                const c = w.globals.seriesCandleC[seriesIndex]?.[dataPointIndex];
                if (o == null) return '';
                const isGreen = c >= o;
                const color = isGreen ? '#26a69a' : '#ef5350';
                const arrow = isGreen ? '▲' : '▼';
                const change = (c - o).toFixed(2);
                const pct = (((c - o) / o) * 100).toFixed(2);
                return `<div class="chart-tooltip">
                    <div class="tooltip-row"><span class="tt-lbl">O</span><span>${o.toFixed(2)}</span></div>
                    <div class="tooltip-row"><span class="tt-lbl">H</span><span>${h.toFixed(2)}</span></div>
                    <div class="tooltip-row"><span class="tt-lbl">L</span><span>${l.toFixed(2)}</span></div>
                    <div class="tooltip-row"><span class="tt-lbl">C</span><span style="color:${color}">${c.toFixed(2)}</span></div>
                    <div class="tooltip-change" style="color:${color}">${arrow} ${change} (${pct}%)</div>
                </div>`;
            },
        },
        annotations: priceInfo
            ? {
                  yaxis: [
                      {
                          y: priceInfo.lastClose,
                          borderColor: priceInfo.isUp ? '#26a69a' : '#ef5350',
                          strokeDashArray: 3,
                          opacity: 0.6,
                          label: {
                              text: priceInfo.lastClose.toFixed(2),
                              style: {
                                  color: '#fff',
                                  background: priceInfo.isUp ? '#26a69a' : '#ef5350',
                                  fontSize: '11px',
                                  padding: { top: 2, bottom: 2, left: 4, right: 4 },
                              },
                              position: 'right',
                              offsetX: -4,
                          },
                      },
                  ],
              }
            : {},
    };

    return (
        <div className="chart-widget-container">
            <div className="chart-header">
                <span className="chart-ticker">{selectedStock || '—'}</span>
                {priceInfo ? (
                    <>
                        <span className="chart-price">{priceInfo.lastClose.toFixed(2)}</span>
                        <span className={`chart-change ${priceInfo.isUp ? 'up' : 'down'}`}>
                            {priceInfo.isUp ? '+' : ''}
                            {priceInfo.change.toFixed(2)} ({priceInfo.isUp ? '+' : ''}
                            {priceInfo.pct.toFixed(2)}%)
                        </span>
                        <span className="chart-ohlc">
                            O&nbsp;{priceInfo.last.y[0].toFixed(2)}&nbsp;&nbsp;H&nbsp;
                            {priceInfo.last.y[1].toFixed(2)}&nbsp;&nbsp;L&nbsp;
                            {priceInfo.last.y[2].toFixed(2)}&nbsp;&nbsp;C&nbsp;
                            {priceInfo.last.y[3].toFixed(2)}
                        </span>
                    </>
                ) : (
                    <span className="chart-waiting">Waiting for data…</span>
                )}
            </div>
            {candlestickData.length > 0 ? (
                <div className="chart-body">
                    <ReactApexChart
                        options={options}
                        series={[{ data: candlestickData }]}
                        type="candlestick"
                        width="100%"
                        height="100%"
                    />
                </div>
            ) : (
                <div className="no-data-message">
                    <span>No market data yet</span>
                </div>
            )}
        </div>
    );
};

export default ChartWidget;
